import { cs } from '../../../lib/currency';
import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { XCircle, Loader2, AlertTriangle, DollarSign } from 'lucide-react';
import api from '../../../services/api';
import { toast } from 'sonner';
import { extractErrorMessage } from './utils.jsx';
import RefundDialog from '../RefundDialog';

/**
 * Two-step Void Invoice flow:
 *   Step 1 — Type-aware reminder modal: tells the admin what follow-up
 *            action they need to take (cancel the lane, remove the
 *            player, etc.) before any DB change.
 *   Step 2 — Reason + confirm: final void with optional account credit.
 *
 * The reminder text is per-invoice-type so admins don't accidentally
 * leave half-finished cleanup (e.g. voiding a lane invoice without
 * cancelling the lane booking, which keeps the slot blocked).
 */
const REMINDERS = {
  lane_booking: {
    title: 'Cancel the lane booking too',
    body:
      'After voiding this invoice, also cancel the lane booking from the ' +
      'Lane Bookings page to free the slot. Otherwise the booking will ' +
      'continue to block that lane on the schedule.',
  },
  small_group: {
    title: 'Remove the player from the session',
    body:
      'After voiding, remove the player from the small-group session ' +
      '(Session Roster). Otherwise the session will still count them as ' +
      'enrolled and may regenerate the invoice next cycle.',
  },
  enrollment: {
    title: 'Confirm the enrollment should keep billing',
    body:
      "Voiding this monthly invoice only cancels the current cycle's " +
      'charge. The enrollment will still generate a new invoice next ' +
      'month. Cancel or pause the enrollment if you want to stop ' +
      'future billing.',
  },
  tournament: {
    title: 'Remove the player registration too',
    body:
      'After voiding, archive the player\'s tournament registration ' +
      '(Tournament → Registrations → Archive). The registration record ' +
      'will be preserved for audit, the player removed from the team, ' +
      'and they will not be able to RSVP. The void can be undone as long ' +
      'as the registration is not archived.',
  },
  manual: {
    title: 'Heads up',
    body:
      'Voiding a manual invoice removes it from outstanding balances. ' +
      'Make sure any external bookkeeping is also updated.',
  },
};

export default function VoidInvoiceDialog({ open, onOpenChange, invoice, onVoided }) {
  const [step, setStep] = useState('reminder'); // 'refund-prompt' | 'reminder' | 'confirm'
  const [reason, setReason] = useState('');
  const [issueCredit, setIssueCredit] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showRefund, setShowRefund] = useState(false);

  // Does this invoice have money collected (any method) that is not yet
  // fully refunded? NOTE: the invoice-list serializer can report
  // amount_paid == face amount on UNPAID overdue rows, and amount_charged
  // also equals the face amount — neither is a reliable "collected" signal.
  // So we gate strictly on a real paid STATUS or an applied account credit.
  const statusLc = String(invoice?.status || invoice?.payment_status || '').toLowerCase();
  const isPaidStatus = ['paid', 'partial', 'partially_refunded', 'deposit_paid'].includes(statusLc);
  const creditApplied = Number(invoice?.credit_applied ?? 0) || 0;
  const amountPaid = Number(invoice?.amount_paid ?? 0) || 0;
  const amountCharged = Number(invoice?.amount_charged ?? 0) || 0;
  const baseAmount = Number(invoice?.amount ?? invoice?.total_amount ?? 0) || 0;
  const surcharge = Number(invoice?.cc_surcharge ?? invoice?.surcharge_amount ?? 0) || 0;
  const invoiceTotal = Math.round((baseAmount + surcharge) * 100) / 100;
  const isFullyPaid = ['paid', 'partially_refunded'].includes(statusLc);
  // Total money collected on this invoice (card/cash + account credit).
  // amount_paid/amount_charged already INCLUDE the credit-covered portion,
  // so we must NOT add credit on top (that double-counted, e.g. $165 + $80
  // => $245). A fully-paid invoice collected its whole total; a partial one
  // collected whatever real money + credit has been applied so far.
  const collectedTotal = isFullyPaid
    ? Math.max(invoiceTotal, amountPaid, amountCharged, creditApplied)
    : Math.round((Math.max(amountPaid, amountCharged) + (amountPaid > 0.005 || amountCharged > 0.005 ? 0 : creditApplied)) * 100) / 100;
  const paidAmount = collectedTotal;
  const fullyRefunded = statusLc === 'refunded' || String(invoice?.refund_status || '').toLowerCase() === 'fully_refunded';
  const hasPayment = !!invoice && !fullyRefunded && (isPaidStatus || creditApplied > 0.005);

  useEffect(() => {
    if (open) {
      setStep(hasPayment ? 'refund-prompt' : 'reminder');
      setReason('');
      setIssueCredit(false);
      setShowRefund(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Build the refund context (mirrors ParentInvoicesPanel) so the nested
  // RefundDialog works for EVERY invoice type.
  const refundContext = useMemo(() => {
    if (!invoice) return null;
    const TYPE_MAP = {
      small_group: 'small_group_invoice',
      enrollment: 'enrollment',
      tournament: 'tournament_registration',
      lane_booking: 'lane_booking',
      manual: 'manual_invoice',
      camp: 'camp_registration',
      camp_registration: 'camp_registration',
    };
    const entityType = TYPE_MAP[invoice.type] || invoice.type;
    const entityId = invoice.type === 'small_group'
      ? String(invoice.id).replace(/^sg-/, '')
      : invoice.id;
    return {
      entityType,
      entity: {
        id: entityId,
        price: Number(invoice.amount_charged) > 0 ? Number(invoice.amount_charged) : invoice.amount,
        refunded_amount: invoice.refunded_amount || 0,
        customer_name: invoice.parent_name,
        payment_method: invoice.payment_method,
        monthly_invoice_id: invoice.monthly_invoice_id || undefined,
      },
      summaryLines: [
        { label: 'Customer', value: invoice.parent_name || '—' },
        { label: 'Description', value: invoice.description || '—' },
        { label: 'Type', value: invoice.type_label || invoice.type },
      ],
    };
  }, [invoice]);

  const reminder = invoice ? REMINDERS[invoice.type] || REMINDERS.manual : null;

  const handleSubmit = async () => {
    if (!invoice) return;
    setProcessing(true);
    try {
      await api.post('/admin/invoices/void', {
        invoice_id: invoice.id,
        invoice_type: invoice.type,
        reason: reason || null,
        credit_balance: issueCredit,
        // Admin reached this confirm step through the refund-first prompt,
        // so intentional voids of paid/refunded invoices are allowed.
        allow_settled: true,
      });
      toast.success('Invoice voided successfully');
      onOpenChange(false);
      onVoided && onVoided();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Failed to void invoice'));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md w-[calc(100vw-2rem)] overflow-hidden" data-testid="void-invoice-dialog">
        {step === 'refund-prompt' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-amber-600" />
                This invoice has a payment
              </DialogTitle>
              <DialogDescription>
                Money was collected on this invoice. Would you like to refund it
                before voiding? If you refund, the refund is processed first and
                then the void continues.
              </DialogDescription>
            </DialogHeader>
            {invoice && (
              <div className="bg-slate-50 p-3 rounded-lg space-y-1 w-full min-w-0">
                <p className="font-medium break-words">{invoice.description}</p>
                <p className="text-sm text-slate-500 break-words">{invoice.parent_name}</p>
                <p className="text-sm font-semibold break-words">
                  Collected: {cs()}{Number(collectedTotal || invoice.amount || 0).toFixed(2)}
                  {creditApplied > 0.005 && (
                    <span className="font-normal text-slate-500"> (incl. {cs()}{creditApplied.toFixed(2)} account credit)</span>
                  )}
                </p>
              </div>
            )}
            <DialogFooter className="flex flex-col gap-2 sm:flex-col sm:space-x-0">
              <Button
                onClick={() => setShowRefund(true)}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="void-process-refund-btn"
              >
                <DollarSign className="h-4 w-4 mr-1" />
                Refund, then void
              </Button>
              <Button variant="outline" className="w-full" onClick={() => setStep('reminder')} data-testid="void-skip-refund-btn">
                No, just void
              </Button>
              <Button variant="outline" className="w-full" onClick={() => onOpenChange(false)} data-testid="void-refund-cancel-btn">
                Cancel
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'reminder' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                {reminder?.title || 'Heads up'}
              </DialogTitle>
              <DialogDescription>
                Please complete the follow-up action below after voiding to
                keep the data consistent.
              </DialogDescription>
            </DialogHeader>
            {invoice && (
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                  <p className="text-amber-900 text-sm">{reminder?.body}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg space-y-1">
                  <p className="font-medium">{invoice.description}</p>
                  <p className="text-sm text-slate-500">{invoice.parent_name}</p>
                  <p className="text-sm font-semibold">{cs()}{invoice.amount?.toFixed(2)}</p>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="void-cancel-btn">
                Cancel
              </Button>
              <Button onClick={() => setStep('confirm')} data-testid="void-acknowledge-btn">
                I understand — continue
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'confirm' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-600" />
                Void Invoice
              </DialogTitle>
              <DialogDescription>
                Cancel this invoice. It will be marked as not collectible and
                excluded from outstanding-balance totals.
              </DialogDescription>
            </DialogHeader>
            {invoice && (
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                  <p className="text-red-800 font-medium">Are you sure you want to void this invoice?</p>
                  <p className="text-red-600 text-sm mt-1">This action marks the invoice as cancelled and not collectible.</p>
                </div>

                <div className="bg-slate-50 p-3 rounded-lg space-y-1">
                  <p className="font-medium">{invoice.description}</p>
                  <p className="text-sm text-slate-500">{invoice.parent_name}</p>
                  <p className="text-sm font-semibold">{cs()}{invoice.amount?.toFixed(2)}</p>
                </div>

                <div>
                  <Label>Reason for voiding (optional)</Label>
                  <Textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g., Duplicate invoice, Customer cancelled, etc."
                    rows={2}
                    data-testid="void-reason-input"
                  />
                </div>

                {(invoice.amount_paid > 0 || invoice.payment_status === 'partial') && (
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={issueCredit}
                      onChange={(e) => setIssueCredit(e.target.checked)}
                      data-testid="void-issue-credit-checkbox"
                    />
                    <span>
                      Issue account credit equal to the amount already paid
                      <span className="block text-xs text-slate-500">
                        Adds {cs()}{(invoice.amount_paid ?? invoice.amount)?.toFixed(2)} to the parent's credit balance to apply to a future cycle.
                      </span>
                    </span>
                  </label>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep('reminder')} data-testid="void-back-btn">
                Back
              </Button>
              <Button onClick={handleSubmit} disabled={processing} variant="destructive" data-testid="void-confirm-btn">
                {processing ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Voiding...</>
                ) : (
                  <><XCircle className="h-4 w-4 mr-2" />Void Invoice</>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>

      {refundContext && (
        <RefundDialog
          open={showRefund}
          onOpenChange={setShowRefund}
          entityType={refundContext.entityType}
          entity={refundContext.entity}
          summaryLines={refundContext.summaryLines}
          allowCancelCheckbox={false}
          onRefunded={() => {
            // Refund done — continue the void workflow (refund then void).
            setShowRefund(false);
            toast.success('Refund processed — now confirm the void.');
            setStep('reminder');
          }}
        />
      )}
    </Dialog>
  );
}
